import Phaser from 'phaser';
import { BOARD_CONTENT_FILL_RATIO, BOARD_SAFE_FIT_RATIO } from '../../config/constants';
import { BOARD_PIECE_TEXTURE_SUFFIX } from '../pieces/PieceTextureFactory';

export type BoardRect = {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
};

export type SnapWorldResult = { row: number; col: number; snapX: number; snapY: number } | null;

export type PieceLike = { id: number; row: number; col: number };

export type SeamSide = 'top' | 'right' | 'bottom' | 'left';

export type BoardControllerCallbacks = {
  /** Плавающая «+1» над деталью при правильной постановке. */
  playFloatingCheckmark: (worldX: number, worldY: number) => void;
  /** Свечение вдоль швов при склейке с уже поставленными соседями (опционально). */
  playSeamGlow?: (args: {
    pieceId: number;
    worldX: number;
    worldY: number;
    sides: SeamSide[];
    pieceW: number;
    pieceH: number;
  }) => void;
  /** Обновляет отображение прогресса (сцена вызывает progress.update + progress.getText в DOM). Завершение по «всё собрано» сцена обрабатывает через PuzzleProgress.onCompleted. */
  updatePuzzleProgressDOM: () => void;
  /** Сохранить состояние пазла (debounce/flush контролируется сценой). */
  saveState: () => void;
  /** Возврат куска с доски в лоток (создание нового Image в лотке). */
  returnBoardPieceToTray: (pieceId: number, pieceKey: string, tileW: number, tileH: number) => void;
  /** Срабатывает при успешной "магнитной" склейке соседних незакреплённых кусочков. */
  onMagnetJoin?: () => void;
};

/**
 * Инкапсулирует всю логику "доски":
 * - геометрия boardRect, worldToCell/cellToWorld/snapWorld
 * - состояние placedPieces/placedCorrectPieceIds/targetsByPieceId
 * - создание слоя placedLayer и размещение деталей на доске
 * - snap/dragend ограничения и возврат в лоток
 *
 * Важно: поведение должно оставаться идентичным PuzzleScene.ts.
 */
export class BoardController {
  private static readonly BOARD_DRAG_START_PX_MOUSE = 6;
  private static readonly BOARD_DRAG_START_PX_TOUCH = 4;
  private static readonly BOARD_HIT_EXPAND_PX = 28;
  private static readonly BOARD_FIXED_PIECE_DEPTH = 5000;
  private static readonly BOARD_FREE_PIECE_DEPTH = 5500;
  private static readonly BOARD_DRAG_PIECE_DEPTH = 6000;

  private scene: Phaser.Scene;
  private callbacks: BoardControllerCallbacks;
  private snapRadius: number;

  private board: Phaser.GameObjects.Container | null = null;
  private placedLayer: Phaser.GameObjects.Container | null = null;
  private boardDebugGraphics: Phaser.GameObjects.Graphics | null = null;
  private boardHighlightSprite: Phaser.GameObjects.Image | null = null;
  private boardVignetteSprite: Phaser.GameObjects.Image | null = null;
  private boardNoiseSprite: Phaser.GameObjects.Image | null = null;
  private boardNoiseMask: Phaser.GameObjects.Graphics | null = null;
  private boardViewportMaskGraphics: Phaser.GameObjects.Graphics | null = null;
  private boardViewportMask: Phaser.Display.Masks.GeometryMask | null = null;
  private boardMaskTemporarilyDisabled = false;

  private boardRows = 0;
  private boardCols = 0;
  private boardRect: BoardRect | null = null;
  private boardCellW = 0;
  private boardCellH = 0;
  private boardPieceW = 0;
  private boardPieceH = 0;
  private totalPieces = 0;

  /** pieceId -> { row, col } для поставленных деталей. */
  private placedPieces = new Map<number, { row: number; col: number }>();
  /** Идентификаторы деталей, поставленных в правильное место (заблокированы). */
  private placedCorrectPieceIds = new Set<number>();
  /** pieceId -> правильная позиция (центр ячейки) для слабого снапа. */
  private targetsByPieceId = new Map<number, { targetX: number; targetY: number }>();

  /** Порог перекрытия площади: если незафиксированный кусок перекрывает слот больше чем на это — возвращаем в лоток. */
  private static readonly OVERLAP_EVICT_THRESHOLD = 0.35;

  /** Текущая перетаскиваемая группа (правильно собранные соседи). null между драг-сессиями. */
  private dragGroup: number[] | null = null;
  /** Позиции кусочков группы в момент начала перетаскивания (для сдвига на delta). */
  private dragGroupStartPositions = new Map<number, { x: number; y: number }>();
  /** Ручной drag на доске (fallback вместо Phaser drag, который нестабилен на части touch-девайсов). */
  private boardDragPointerId: number | null = null;
  private boardDragCandidatePieceId: number | null = null;
  private boardDragAnchorPieceId: number | null = null;
  private boardDragStartPointerX = 0;
  private boardDragStartPointerY = 0;
  private boardDragStarted = false;
  private viewportScale = 1;
  private viewportOffsetX = 0;
  private viewportOffsetY = 0;
  private isInteractionBlocked: (() => boolean) | null = null;

  constructor(scene: Phaser.Scene, args: { snapRadius: number; callbacks: BoardControllerCallbacks }) {
    this.scene = scene;
    this.snapRadius = args.snapRadius;
    this.callbacks = args.callbacks;
  }

  /** Временно отключает маску доски во время drag, чтобы куски были видны за границами рабочей области. */
  private setBoardMaskTemporarilyDisabled(disabled: boolean): void {
    if (!this.board) return;
    if (disabled) {
      if (this.boardMaskTemporarilyDisabled) return;
      this.board.clearMask();
      this.boardMaskTemporarilyDisabled = true;
      return;
    }
    if (!this.boardMaskTemporarilyDisabled) return;
    if (this.boardViewportMask) {
      this.board.setMask(this.boardViewportMask);
    }
    this.boardMaskTemporarilyDisabled = false;
  }

  /**
   * При zoom/pan маску нельзя снимать: иначе доска "вылезает" поверх лотка и показывает
   * скрытые области. В обычном масштабе временное снятие маски во время drag допустимо.
   */
  private canTemporarilyDisableBoardMaskForDrag(): boolean {
    const scaleIsBase = Math.abs(this.viewportScale - 1) < 1e-4;
    const offsetIsBase =
      Math.abs(this.viewportOffsetX) < 1e-4 && Math.abs(this.viewportOffsetY) < 1e-4;
    return scaleIsBase && offsetIsBase;
  }

  /**
   * Внутри Phaser.Container порядок отрисовки идёт по list.
   * depth у дочерних объектов контейнера не гарантирует визуальный z-order.
   */
  private syncContainerOrder(img: Phaser.GameObjects.Image, place: 'fixed' | 'free' | 'drag'): void {
    if (!this.placedLayer) return;
    if (place === 'fixed') this.placedLayer.sendToBack(img);
    else this.placedLayer.bringToTop(img);
  }

  /** Обновить callbacks без пересоздания контроллера. */
  setCallbacks(callbacks: BoardControllerCallbacks): void {
    this.callbacks = callbacks;
  }

  /** Блокировка ручного drag по доске (например во время pinch/pan viewport). */
  setInteractionBlockedPredicate(predicate: (() => boolean) | null): void {
    this.isInteractionBlocked = predicate;
  }

  /** Навешивает обработчики drag для кусочков, находящихся на доске. */
  attachInputHandlers(): void {
    // Стартовый порог drag для мобильных по умолчанию (дальше корректируем динамически на pointerdown).
    this.scene.input.dragDistanceThreshold = BoardController.BOARD_DRAG_START_PX_TOUCH;

    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isInteractionBlocked?.()) return;
      const ev = pointer.event as unknown as { pointerType?: string } | undefined;
      const isTouch = ev?.pointerType === 'touch' || (pointer as unknown as { wasTouch?: boolean }).wasTouch === true;
      const p = this.screenToBoard(pointer.worldX, pointer.worldY);
      this.scene.input.dragDistanceThreshold = isTouch
        ? BoardController.BOARD_DRAG_START_PX_TOUCH
        : BoardController.BOARD_DRAG_START_PX_MOUSE;
      this.boardDragPointerId = pointer.id;
      this.boardDragStartPointerX = p.x;
      this.boardDragStartPointerY = p.y;
      this.boardDragStarted = false;
      this.boardDragAnchorPieceId = null;
      this.boardDragCandidatePieceId = this.pickBoardPieceUnderPointer(p.x, p.y);
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.boardDragPointerId == null || pointer.id !== this.boardDragPointerId) return;
      if (this.isInteractionBlocked?.()) return;
      if (this.boardDragCandidatePieceId == null) return;
      const p = this.screenToBoard(pointer.worldX, pointer.worldY);

      const ev = pointer.event as unknown as { pointerType?: string } | undefined;
      const isTouch = ev?.pointerType === 'touch' || (pointer as unknown as { wasTouch?: boolean }).wasTouch === true;
      const dragThreshold = isTouch
        ? BoardController.BOARD_DRAG_START_PX_TOUCH
        : BoardController.BOARD_DRAG_START_PX_MOUSE;

      if (!this.boardDragStarted) {
        const dist = Phaser.Math.Distance.Between(
          this.boardDragStartPointerX,
          this.boardDragStartPointerY,
          p.x,
          p.y
        );
        if (dist <= dragThreshold) return;

        this.boardDragStarted = true;
        this.boardDragAnchorPieceId = this.boardDragCandidatePieceId;
        const group = this.getGroupForPiece(this.boardDragAnchorPieceId);
        this.dragGroup = group;
        this.dragGroupStartPositions.clear();
        for (const id of group) {
          const img = this.findPlacedImageByPieceId(id);
          if (img) {
            this.dragGroupStartPositions.set(id, { x: img.x, y: img.y });
            img.setDepth(BoardController.BOARD_DRAG_PIECE_DEPTH);
            this.syncContainerOrder(img, 'drag');
          }
        }
        if (this.canTemporarilyDisableBoardMaskForDrag()) {
          this.setBoardMaskTemporarilyDisabled(true);
        }
        const anchorImg = this.findPlacedImageByPieceId(this.boardDragAnchorPieceId);
        if (anchorImg) {
          this.scene.input.emit('dragstart', pointer, anchorImg);
        }
      }

      const anchorId = this.boardDragAnchorPieceId;
      if (!this.boardDragStarted || anchorId == null) return;
      const start = this.dragGroupStartPositions.get(anchorId);
      if (!start || !this.dragGroup) return;
      const deltaX = p.x - this.boardDragStartPointerX;
      const deltaY = p.y - this.boardDragStartPointerY;
      for (const id of this.dragGroup) {
        const pos = this.dragGroupStartPositions.get(id);
        if (!pos) continue;
        const img = this.findPlacedImageByPieceId(id);
        if (img) img.setPosition(pos.x + deltaX, pos.y + deltaY);
      }
    });

    const finishManualBoardDrag = (pointer: Phaser.Input.Pointer): void => {
      if (this.boardDragPointerId == null || pointer.id !== this.boardDragPointerId) return;
      try {
        if (this.boardDragStarted && this.boardDragAnchorPieceId != null) {
          const anchorImg = this.findPlacedImageByPieceId(this.boardDragAnchorPieceId);
          if (anchorImg) {
            this.handleBoardDragEnd(anchorImg);
            this.scene.input.emit('dragend', pointer, anchorImg);
          }
        }
      } finally {
        this.setBoardMaskTemporarilyDisabled(false);
      }
      this.boardDragPointerId = null;
      this.boardDragCandidatePieceId = null;
      this.boardDragAnchorPieceId = null;
      this.boardDragStarted = false;
    };

    this.scene.input.on('pointerup', finishManualBoardDrag);
    this.scene.input.on('pointerupoutside', finishManualBoardDrag);
  }

  destroy(): void {
    this.dragGroup = null;
    this.dragGroupStartPositions.clear();
    this.placedPieces.clear();
    this.targetsByPieceId.clear();
    this.placedCorrectPieceIds.clear();
    this.totalPieces = 0;
    this.boardRect = null;
    this.boardRows = 0;
    this.boardCols = 0;
    this.boardCellW = 0;
    this.boardCellH = 0;
    this.boardPieceW = 0;
    this.boardPieceH = 0;
    this.viewportScale = 1;
    this.viewportOffsetX = 0;
    this.viewportOffsetY = 0;

    if (this.placedLayer) {
      this.placedLayer.destroy();
      this.placedLayer = null;
    }
    if (this.boardDebugGraphics) {
      this.boardDebugGraphics.destroy();
      this.boardDebugGraphics = null;
    }
    if (this.boardHighlightSprite) {
      this.boardHighlightSprite.destroy();
      this.boardHighlightSprite = null;
    }
    if (this.boardVignetteSprite) {
      this.boardVignetteSprite.destroy();
      this.boardVignetteSprite = null;
    }
    if (this.boardNoiseSprite) {
      this.boardNoiseSprite.destroy();
      this.boardNoiseSprite = null;
    }
    if (this.boardNoiseMask) {
      this.boardNoiseMask.destroy();
      this.boardNoiseMask = null;
    }
    if (this.boardViewportMask) {
      this.boardViewportMask.destroy();
      this.boardViewportMask = null;
    }
    this.boardMaskTemporarilyDisabled = false;
    if (this.boardViewportMaskGraphics) {
      this.boardViewportMaskGraphics.destroy();
      this.boardViewportMaskGraphics = null;
    }
    if (this.scene.textures.exists('board-noise')) this.scene.textures.remove('board-noise');
    if (this.scene.textures.exists('board-highlight')) this.scene.textures.remove('board-highlight');
    if (this.scene.textures.exists('board-vignette')) this.scene.textures.remove('board-vignette');
    if (this.board) {
      this.board.destroy();
      this.board = null;
    }
  }

  getRect(): BoardRect | null {
    return this.boardRect;
  }

  /** Базовый rect доски (без viewport-трансформации). */
  getBaseRectBox(): { x: number; y: number; w: number; h: number } | null {
    const r = this.boardRect;
    if (!r) return null;
    return { x: r.x, y: r.y, w: r.w, h: r.h };
  }

  hasPlacedLayer(): boolean {
    return this.placedLayer != null;
  }

  getTotalPieces(): number {
    return this.totalPieces;
  }
  getPlacedCorrectCount(): number {
    return this.placedCorrectPieceIds.size;
  }
  /** Убрать кусок из board-состояния (для возврата в лоток). */
  unplacePiece(pieceId: number): void {
    this.placedPieces.delete(pieceId);
    this.placedCorrectPieceIds.delete(pieceId);
  }
  isPlaced(pieceId: number): boolean {
    return this.placedPieces.has(pieceId);
  }
  getCellSize(): { w: number; h: number } {
    return { w: this.boardCellW, h: this.boardCellH };
  }
  getPieceSize(): { w: number; h: number } {
    return { w: this.boardPieceW, h: this.boardPieceH };
  }
  getCellH(): number {
    return this.boardCellH;
  }

  /** Для CustomDragController: rect без cx/cy. */
  getRectBox(): { x: number; y: number; w: number; h: number } | null {
    const r = this.boardRect;
    if (!r) return null;
    return this.toScreenRect(r);
  }

  /** Есть ли под экранной точкой свободный (не зафиксированный) кусок на доске. */
  hasDraggablePieceAtScreenPoint(x: number, y: number): boolean {
    const p = this.screenToBoard(x, y);
    return this.pickBoardPieceUnderPointer(p.x, p.y) != null;
  }

  getViewportTransform(): { scale: number; offsetX: number; offsetY: number } {
    return {
      scale: this.viewportScale,
      offsetX: this.viewportOffsetX,
      offsetY: this.viewportOffsetY,
    };
  }

  setViewportTransform(scale: number, offsetX: number, offsetY: number): void {
    this.viewportScale = Number.isFinite(scale) ? scale : 1;
    this.viewportOffsetX = Number.isFinite(offsetX) ? offsetX : 0;
    this.viewportOffsetY = Number.isFinite(offsetY) ? offsetY : 0;
    if (!this.board) return;
    this.board.setScale(this.viewportScale);
    this.board.setPosition(this.viewportOffsetX, this.viewportOffsetY);
  }

  screenToBoard(x: number, y: number): { x: number; y: number } {
    const s = this.viewportScale || 1;
    return {
      x: (x - this.viewportOffsetX) / s,
      y: (y - this.viewportOffsetY) / s,
    };
  }

  private toScreenRect(r: { x: number; y: number; w: number; h: number }): { x: number; y: number; w: number; h: number } {
    return {
      x: r.x * this.viewportScale + this.viewportOffsetX,
      y: r.y * this.viewportScale + this.viewportOffsetY,
      w: r.w * this.viewportScale,
      h: r.h * this.viewportScale,
    };
  }

  /** Rect правильной ячейки для кусочка (центр targetX/targetY, размер кусочка). Для туториала. */
  getCorrectTargetRect(pieceId: number): { x: number; y: number; w: number; h: number } | null {
    const target = this.targetsByPieceId.get(pieceId);
    if (!target) return null;
    const w = this.boardPieceW;
    const h = this.boardPieceH;
    return {
      x: target.targetX - w / 2,
      y: target.targetY - h / 2,
      w,
      h,
    };
  }

  /** Ключ текстуры для отображения на доске (ослабленная тень), иначе базовый pieceKey. */
  private getTextureKeyForBoard(pieceKey: string): string {
    const boardKey = pieceKey + BOARD_PIECE_TEXTURE_SUFFIX;
    return this.scene.textures.exists(boardKey) ? boardKey : pieceKey;
  }

  /** Прямоугольник рабочей области в координатах мира/камеры (для ограничения drag). */
  getBoardRectGeom(): Phaser.Geom.Rectangle | null {
    const r = this.boardRect;
    if (!r) return null;
    return new Phaser.Geom.Rectangle(r.x, r.y, r.w, r.h);
  }

  /**
   * Мировая точка -> ячейка сетки доски (row, col). Clamp в [0..rows-1], [0..cols-1].
   */
  worldToCell(x: number, y: number): { row: number; col: number } | null {
    const r = this.boardRect;
    if (!r || this.boardRows <= 0 || this.boardCols <= 0) return null;
    const cellW = r.w / this.boardCols;
    const cellH = r.h / this.boardRows;
    const col = Math.floor((x - r.x) / cellW);
    const row = Math.floor((y - r.y) / cellH);
    return {
      row: Phaser.Math.Clamp(row, 0, this.boardRows - 1),
      col: Phaser.Math.Clamp(col, 0, this.boardCols - 1),
    };
  }

  /**
   * Ячейка сетки -> мировая точка (левый верх ячейки).
   */
  cellToWorld(row: number, col: number): { x: number; y: number } | null {
    const r = this.boardRect;
    if (!r || this.boardRows <= 0 || this.boardCols <= 0) return null;
    const cellW = r.w / this.boardCols;
    const cellH = r.h / this.boardRows;
    return {
      x: r.x + col * cellW,
      y: r.y + row * cellH,
    };
  }

  /**
   * Ближайшая ячейка к мировой точке и её центр (snapX, snapY) — центр ячейки.
   */
  snapWorld(x: number, y: number): SnapWorldResult {
    const p = this.screenToBoard(x, y);
    const cell = this.worldToCell(p.x, p.y);
    if (!cell) return null;
    const pos = this.cellToWorld(cell.row, cell.col);
    if (!pos) return null;
    const cellW = this.boardRect!.w / this.boardCols;
    const cellH = this.boardRect!.h / this.boardRows;
    return {
      row: cell.row,
      col: cell.col,
      snapX: pos.x + cellW / 2,
      snapY: pos.y + cellH / 2,
    };
  }

  /**
   * Строит геометрию доски, цели снапа и слои. Эквивалент коду из PuzzleScene.buildPuzzlePieces().
   */
  build(args: {
    safeRect: { left: number; top: number; width: number; height: number };
    imageWidth: number;
    imageHeight: number;
    rows: number;
    cols: number;
    pieces: PieceLike[];
    tabRatio: number;
  }): void {
    // В исходном коде destroyBoardAndTray вызывается снаружи до build,
    // но для безопасности держим destroy тут (идемпотентно).
    this.destroy();

    const cam = this.scene.cameras.main;
    const boardCx = cam.width / 2;
    const boardCy = args.safeRect.top + args.safeRect.height / 2;
    this.board = this.scene.add.container(0, 0);
    this.board.setScrollFactor(0);
    this.board.setDepth(49998);

    this.boardRows = args.rows;
    this.boardCols = args.cols;
    const scale = Math.min(
      (args.safeRect.width * BOARD_SAFE_FIT_RATIO) / args.imageWidth,
      (args.safeRect.height * BOARD_SAFE_FIT_RATIO) / args.imageHeight
    );
    const displayW = args.imageWidth * scale;
    const displayH = args.imageHeight * scale;
    const boardW = displayW * BOARD_CONTENT_FILL_RATIO;
    const boardH = displayH * BOARD_CONTENT_FILL_RATIO;
    this.boardRect = {
      x: boardCx - boardW / 2,
      y: boardCy - boardH / 2,
      w: boardW,
      h: boardH,
      cx: boardCx,
      cy: boardCy,
    };

    const cellW = boardW / args.cols;
    const cellH = boardH / args.rows;
    const tab = Math.max(0, Math.min(cellW, cellH) * Math.max(0, args.tabRatio));
    this.boardCellW = cellW;
    this.boardCellH = cellH;
    this.boardPieceW = cellW + tab * 2;
    this.boardPieceH = cellH + tab * 2;
    this.totalPieces = args.rows * args.cols;
    this.targetsByPieceId.clear();
    this.placedCorrectPieceIds.clear();
    args.pieces.forEach((piece) => {
      const targetX = this.boardRect!.x + (piece.col + 0.5) * cellW;
      const targetY = this.boardRect!.y + (piece.row + 0.5) * cellH;
      this.targetsByPieceId.set(piece.id, { targetX, targetY });
    });

    if (this.boardDebugGraphics) {
      this.boardDebugGraphics.destroy();
      this.boardDebugGraphics = null;
    }
    this.boardDebugGraphics = this.scene.add
      .graphics()
      .setScrollFactor(0)
      .setDepth(49998);
    const { x, y, w, h } = this.boardRect;
    const cornerRadius = 14;
    const shadowBlur = 15;
    const shadowOffsetY = 8;
    const shadowAlpha = 0.2;
    const shadowRadiusOuter = cornerRadius + shadowBlur * 0.5;
    const shadowRadiusMid = cornerRadius + shadowBlur * 0.25;
    const shadowRadiusMain = cornerRadius;
    this.boardDebugGraphics.fillStyle(0x000000, shadowAlpha * 0.2);
    this.boardDebugGraphics.fillRoundedRect(
      x - shadowBlur,
      y + shadowOffsetY - 4,
      w + shadowBlur * 2,
      h + shadowBlur,
      shadowRadiusOuter
    );
    this.boardDebugGraphics.fillStyle(0x000000, shadowAlpha * 0.5);
    this.boardDebugGraphics.fillRoundedRect(
      x - shadowBlur * 0.5,
      y + shadowOffsetY,
      w + shadowBlur,
      h + shadowBlur * 0.5,
      shadowRadiusMid
    );
    this.boardDebugGraphics.fillStyle(0x000000, shadowAlpha);
    this.boardDebugGraphics.fillRoundedRect(x, y + shadowOffsetY, w, h, shadowRadiusMain);
    const innerShadowInset = 12;
    this.boardDebugGraphics.fillStyle(0x000000, 0.05);
    this.boardDebugGraphics.fillRoundedRect(
      x - innerShadowInset,
      y - innerShadowInset,
      w + innerShadowInset * 2,
      h + innerShadowInset * 2,
      cornerRadius + innerShadowInset
    );
    // Подложка: тёплый серый (нейтрально, не холодно), низкий контраст
    this.boardDebugGraphics.fillStyle(0x353432, 1);
    this.boardDebugGraphics.fillRoundedRect(x, y, w, h, cornerRadius);
    const innerInset = 4;
    const innerRadius = Math.max(0, cornerRadius - innerInset);
    this.boardDebugGraphics.fillStyle(0x3b3937, 1);
    this.boardDebugGraphics.fillRoundedRect(
      x + innerInset,
      y + innerInset,
      w - innerInset * 2,
      h - innerInset * 2,
      innerRadius
    );
    const innerX = x + innerInset;
    const innerY = y + innerInset;
    const innerW = w - innerInset * 2;
    const innerH = h - innerInset * 2;

    // Маска для highlight и noise (внутренний скруглённый rect)
    if (this.boardNoiseMask) this.boardNoiseMask.destroy();
    this.boardNoiseMask = this.scene.add.graphics().setScrollFactor(0);
    this.boardNoiseMask.fillStyle(0xffffff, 1);
    this.boardNoiseMask.fillRoundedRect(0, 0, innerW, innerH, innerRadius);
    this.boardNoiseMask.setPosition(innerX, innerY);
    this.boardNoiseMask.setVisible(false);
    const innerMask = this.boardNoiseMask.createGeometryMask();

    // Мягкий radial highlight в центре поля (alpha ~0.04–0.06)
    const highlightSize = 256;
    const highlightCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    if (highlightCanvas) {
      highlightCanvas.width = highlightSize;
      highlightCanvas.height = highlightSize;
      const hctx = highlightCanvas.getContext('2d');
      if (hctx) {
        const cx = highlightSize / 2;
        const r = highlightSize / 2;
        const grad = hctx.createRadialGradient(cx, cx, 0, cx, cx, r);
        grad.addColorStop(0, 'rgba(255,255,255,0.05)');
        grad.addColorStop(0.5, 'rgba(255,255,255,0.02)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        hctx.fillStyle = grad;
        hctx.fillRect(0, 0, highlightSize, highlightSize);
      }
      if (this.scene.textures.exists('board-highlight')) this.scene.textures.remove('board-highlight');
      this.scene.textures.addCanvas('board-highlight', highlightCanvas);
      if (this.boardHighlightSprite) this.boardHighlightSprite.destroy();
      this.boardHighlightSprite = this.scene.add
        .image(innerX + innerW / 2, innerY + innerH / 2, 'board-highlight')
        .setDisplaySize(innerW, innerH)
        .setAlpha(1)
        .setScrollFactor(0)
        .setDepth(49998.5);
      this.boardHighlightSprite.setMask(innerMask);
    }

    // Лёгкая внутренняя виньетка: края чуть темнее центра, без чёткой рамки
    const vignetteSize = 256;
    const vignetteCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    if (vignetteCanvas) {
      vignetteCanvas.width = vignetteSize;
      vignetteCanvas.height = vignetteSize;
      const vctx = vignetteCanvas.getContext('2d');
      if (vctx) {
        const cx = vignetteSize / 2;
        const r = vignetteSize / 2;
        const grad = vctx.createRadialGradient(cx, cx, 0, cx, cx, r);
        grad.addColorStop(0, 'rgba(0,0,0,0)');
        grad.addColorStop(0.6, 'rgba(0,0,0,0)');
        grad.addColorStop(0.85, 'rgba(0,0,0,0.02)');
        grad.addColorStop(1, 'rgba(0,0,0,0.045)');
        vctx.fillStyle = grad;
        vctx.fillRect(0, 0, vignetteSize, vignetteSize);
      }
      if (this.scene.textures.exists('board-vignette')) this.scene.textures.remove('board-vignette');
      this.scene.textures.addCanvas('board-vignette', vignetteCanvas);
      if (this.boardVignetteSprite) this.boardVignetteSprite.destroy();
      this.boardVignetteSprite = this.scene.add
        .image(innerX + innerW / 2, innerY + innerH / 2, 'board-vignette')
        .setDisplaySize(innerW, innerH)
        .setAlpha(1)
        .setScrollFactor(0)
        .setDepth(49999.5);
      this.boardVignetteSprite.setMask(innerMask);
    }

    // Лёгкая текстура (без ощущения «бетона»): узкий диапазон, меньшая непрозрачность
    const noiseSize = 256;
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    if (canvas) {
      canvas.width = noiseSize;
      canvas.height = noiseSize;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const idata = ctx.createImageData(noiseSize, noiseSize);
        const d = idata.data;
        for (let i = 0; i < d.length; i += 4) {
          const v = Math.floor(122 + Math.random() * 14);
          d[i] = d[i + 1] = d[i + 2] = v;
          d[i + 3] = 4 + Math.floor(Math.random() * 6);
        }
        ctx.putImageData(idata, 0, 0);
      }
      if (this.scene.textures.exists('board-noise')) this.scene.textures.remove('board-noise');
      this.scene.textures.addCanvas('board-noise', canvas);
      if (this.boardNoiseSprite) this.boardNoiseSprite.destroy();
      this.boardNoiseSprite = this.scene.add
        .image(innerX + innerW / 2, innerY + innerH / 2, 'board-noise')
        .setDisplaySize(innerW, innerH)
        .setAlpha(1)
        .setScrollFactor(0)
        .setDepth(49999);
      this.boardNoiseSprite.setMask(innerMask);
    }

    if (this.placedLayer) {
      this.placedLayer.destroy();
      this.placedLayer = null;
    }
    this.placedLayer = this.scene.add.container(0, 0).setScrollFactor(0).setDepth(50000);
    this.placedPieces.clear();
    this.board.add(this.boardDebugGraphics);
    if (this.boardHighlightSprite) this.board.add(this.boardHighlightSprite);
    if (this.boardNoiseSprite) this.board.add(this.boardNoiseSprite);
    if (this.boardVignetteSprite) this.board.add(this.boardVignetteSprite);
    this.board.add(this.placedLayer);
    if (this.boardNoiseMask) this.board.add(this.boardNoiseMask);
    if (this.boardViewportMask) this.board.clearMask(true);
    if (this.boardViewportMaskGraphics) {
      this.boardViewportMaskGraphics.destroy();
      this.boardViewportMaskGraphics = null;
    }
    this.boardViewportMaskGraphics = this.scene.add.graphics().setScrollFactor(0).setDepth(60000);
    this.boardViewportMaskGraphics.fillStyle(0xffffff, 1);
    this.boardViewportMaskGraphics.fillRoundedRect(x, y, w, h, cornerRadius);
    this.boardViewportMaskGraphics.setVisible(false);
    this.boardViewportMask = this.boardViewportMaskGraphics.createGeometryMask();
    this.board.setMask(this.boardViewportMask);
    this.boardMaskTemporarilyDisabled = false;
    this.setViewportTransform(this.viewportScale, this.viewportOffsetX, this.viewportOffsetY);
  }

  /**
   * Делает кусок на доске интерактивным и перетаскиваемым (Phaser drag).
   */
  makeBoardPieceInteractive(img: Phaser.GameObjects.Image, pieceId: number): void {
    if (!img || !img.scene || !(this.scene as any).input) return;
    img.setData('where', 'board');
    img.setData('pieceId', pieceId);
    img.setDepth(BoardController.BOARD_FREE_PIECE_DEPTH);
    this.syncContainerOrder(img, 'free');
    const tileW = img.displayWidth;
    const tileH = img.displayHeight;
    const expand = BoardController.BOARD_HIT_EXPAND_PX;
    // Используем стандартный hit-test Phaser (как в лотке), но с расширенным прямоугольником.
    // Координаты hitArea задаём относительно центра Image (origin 0.5, 0.5).
    const hitArea = new Phaser.Geom.Rectangle(
      -tileW / 2 - expand,
      -tileH / 2 - expand,
      tileW + expand * 2,
      tileH + expand * 2
    );
    img.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains, true);
    this.scene.input.setDraggable(img);
  }

  private pickBoardPieceUnderPointer(wx: number, wy: number): number | null {
    if (!this.placedLayer) return null;
    const expand = BoardController.BOARD_HIT_EXPAND_PX;
    let bestPieceId: number | null = null;
    let bestListIndex = Number.NEGATIVE_INFINITY;
    for (const obj of this.placedLayer.list as Phaser.GameObjects.GameObject[]) {
      const img = obj as Phaser.GameObjects.Image;
      if (!img?.active || !img.visible) continue;
      const where = img.getData?.('where');
      if (where !== 'board') continue;
      const pieceId = img.getData?.('pieceId') as number | undefined;
      if (pieceId == null) continue;
      if (this.placedCorrectPieceIds.has(pieceId)) continue;
      const halfW = img.displayWidth / 2 + expand;
      const halfH = img.displayHeight / 2 + expand;
      if (Math.abs(wx - img.x) > halfW || Math.abs(wy - img.y) > halfH) continue;
      const listIndex = this.placedLayer.list.indexOf(img);
      if (listIndex > bestListIndex) {
        bestListIndex = listIndex;
        bestPieceId = pieceId;
      }
    }
    return bestPieceId;
  }

  /**
   * Если кусок при постановке на доску уже близко к правильному месту — фиксирует его и обновляет прогресс.
   * dropCell: при дропе из лотка — ячейка, в которую положили; если она совпадает с правильной ячейкой детали,
   * снап и прогресс обновляются без проверки расстояния.
   * Возвращает true, если кусок зафиксирован (не нужно делать интерактивным).
   */
  trySnapPlacedPiece(
    placed: Phaser.GameObjects.Image,
    pieceId: number,
    dropX: number,
    dropY: number,
    dropCell?: { row: number; col: number }
  ): boolean {
    const target = this.targetsByPieceId.get(pieceId);
    if (!target) return false;
    const pieceRow = Math.floor(pieceId / this.boardCols);
    const pieceCol = pieceId % this.boardCols;
    const correctCellMatch =
      dropCell !== undefined &&
      dropCell.row === pieceRow &&
      dropCell.col === pieceCol;
    const dist = Phaser.Math.Distance.Between(dropX, dropY, target.targetX, target.targetY);
    const inRadius = dist < this.snapRadius;
    if (!correctCellMatch && !inRadius) return false;
    placed.setPosition(target.targetX, target.targetY);
    placed.disableInteractive();
    // Только снимаем draggable, если объект им был (снап с доски). При дропе из лотка placed только что создан и не был draggable.
    if (!dropCell) this.scene.input.setDraggable(placed, false);
    placed.setData('where', 'board');
    placed.setData('pieceId', pieceId);
    placed.setData('placedCorrect', true);
    placed.setDepth(BoardController.BOARD_FIXED_PIECE_DEPTH);
    this.syncContainerOrder(placed, 'fixed');
    this.placedCorrectPieceIds.add(pieceId);
    this.callbacks.playFloatingCheckmark(target.targetX, target.targetY);
    this.playOutlineVfx(pieceId, target.targetX, target.targetY, this.getSeamSidesWithCorrectNeighbor(pieceId));
    this.evictPiecesOverlappingSlot(pieceId);
    this.callbacks.updatePuzzleProgressDOM();
    this.callbacks.saveState();
    return true;
  }

  /**
   * Дроп куска из лотка на доску (из CustomDragController).
   * Поведение/порядок операций сохранены 1:1 с прежним кодом в PuzzleScene.buildTrayPanel callbacks.onDropToBoard.
   */
  handleDropFromTray(args: {
    pieceId: number;
    pieceKey: string;
    snap: { row: number; col: number; snapX: number; snapY: number };
    originalImg: Phaser.GameObjects.Image;
    /** Удалить оригинальную картинку из лотка (contentSizer.remove(originalImg, true)). */
    removeOriginalFromTray: (originalImg: Phaser.GameObjects.Image) => void;
    /** Обновить layout/мин-ширину лотка после изменения. */
    updateTrayAfterChange: () => void;
    /** requestAnimationFrame(() => updatePuzzleProgressDOM()) при snapped=true. */
    requestProgressRaf: () => void;
    /** Сохранить состояние после операций с лотком. */
    saveStateAfterDrop: () => void;
    /** Вызывается при успешном snap (кусок поставлен на правильное место). */
    onPiecePlaced?: (pieceId: number) => void;
  }): void {
    if (!this.placedLayer) return;
    const { w: pieceW, h: pieceH } = this.getPieceSize();

    const textureKey = this.getTextureKeyForBoard(args.pieceKey);
    const placed = this.scene.add
      .image(args.snap.snapX, args.snap.snapY, textureKey)
      .setOrigin(0.5, 0.5)
      .setDisplaySize(pieceW, pieceH);
    placed.setData('pieceKey', args.pieceKey);
    placed.setData('tileW', pieceW);
    placed.setData('tileH', pieceH);
    this.placedLayer.add(placed);

    const snapped = this.trySnapPlacedPiece(placed, args.pieceId, args.snap.snapX, args.snap.snapY, {
      row: args.snap.row,
      col: args.snap.col,
    });
    if (!snapped) {
      this.makeBoardPieceInteractive(placed, args.pieceId);
      this.tryMagnetJoin([args.pieceId]);
    }

    this.placedPieces.set(args.pieceId, {
      row: snapped ? Math.floor(args.pieceId / this.boardCols) : args.snap.row,
      col: snapped ? args.pieceId % this.boardCols : args.snap.col,
    });

    args.removeOriginalFromTray(args.originalImg);
    args.updateTrayAfterChange();
    if (snapped) {
      args.requestProgressRaf();
      args.onPiecePlaced?.(args.pieceId);
    }
    args.saveStateAfterDrop();
  }

  /**
   * Восстанавливает состояние (state уже загружен снаружи).
   * Возвращает true, если восстановление было применено (т.е. state != null и нужные слои существуют).
   */
  applySavedState(args: {
    state: Record<string, { where: string; x?: number; y?: number; placedCorrect: boolean }> | null;
    trayContentSizer: any;
    removeFromTraySizer: (trayImg: Phaser.GameObjects.Image) => void;
  }): boolean {
    const state = args.state;
    if (!state || !this.placedLayer || !args.trayContentSizer) return false;

    const content = args.trayContentSizer;
    const children = (content as any).getChildren?.() ?? [];

    for (const [pieceIdStr, entry] of Object.entries(state)) {
      if (entry.where !== 'board') continue;
      const pieceId = Number(pieceIdStr);
      if (Number.isNaN(pieceId)) continue;

      let trayImg: Phaser.GameObjects.Image | null = null;
      for (const child of children) {
        if (child.getData?.('pieceId') === pieceId) {
          trayImg = child as Phaser.GameObjects.Image;
          break;
        }
      }
      if (!trayImg || !trayImg.scene) continue;

      const pieceKey = (trayImg.getData('pieceKey') as string) || (trayImg.texture?.key as string);
      if (!pieceKey || !this.scene.textures.exists(pieceKey)) continue;

      args.removeFromTraySizer(trayImg);

      if (entry.placedCorrect) {
        const target = this.targetsByPieceId.get(pieceId);
        if (target) {
          const textureKey = this.getTextureKeyForBoard(pieceKey);
          const img = this.scene.add
            .image(target.targetX, target.targetY, textureKey)
            .setOrigin(0.5, 0.5)
            .setDisplaySize(this.boardPieceW, this.boardPieceH);
          img.setData('where', 'board');
          img.setData('pieceKey', pieceKey);
          img.setData('pieceId', pieceId);
          img.setData('placedCorrect', true);
          img.disableInteractive();
          img.setDepth(BoardController.BOARD_FIXED_PIECE_DEPTH);
          this.syncContainerOrder(img, 'fixed');
          this.placedCorrectPieceIds.add(pieceId);
          const row = Math.floor(pieceId / this.boardCols);
          const col = pieceId % this.boardCols;
          this.placedPieces.set(pieceId, { row, col });
          this.placedLayer.add(img);
        }
      } else {
        const x = typeof entry.x === 'number' ? entry.x : this.boardRect!.cx;
        const y = typeof entry.y === 'number' ? entry.y : this.boardRect!.cy;
        const textureKey = this.getTextureKeyForBoard(pieceKey);
        const img = this.scene.add
          .image(x, y, textureKey)
          .setOrigin(0.5, 0.5)
          .setDisplaySize(this.boardPieceW, this.boardPieceH);
        img.setData('where', 'board');
        img.setData('pieceKey', pieceKey);
        img.setData('pieceId', pieceId);
        this.makeBoardPieceInteractive(img, pieceId);
        this.placedLayer.add(img);
        const cell = this.worldToCell(x, y);
        if (cell) this.placedPieces.set(pieceId, { row: cell.row, col: cell.col });
      }
    }
    return true;
  }

  /**
   * Собирает текущее состояние всех кусков для сохранения. Эквивалент PuzzleScene.collectCurrentState().
   */
  collectCurrentState(): Record<number, { where: 'board' | 'tray'; x?: number; y?: number; placedCorrect: boolean }> {
    const state: Record<number, { where: 'board' | 'tray'; x?: number; y?: number; placedCorrect: boolean }> = {};
    for (let pieceId = 0; pieceId < this.totalPieces; pieceId++) {
      if (this.placedCorrectPieceIds.has(pieceId)) {
        const t = this.targetsByPieceId.get(pieceId);
        if (t) {
          state[pieceId] = { where: 'board', x: t.targetX, y: t.targetY, placedCorrect: true };
        } else {
          state[pieceId] = { where: 'tray', placedCorrect: false };
        }
      } else if (this.placedPieces.has(pieceId)) {
        const img = this.findPlacedImageByPieceId(pieceId);
        if (img) {
          state[pieceId] = { where: 'board', x: img.x, y: img.y, placedCorrect: false };
        } else {
          state[pieceId] = { where: 'tray', placedCorrect: false };
        }
      } else {
        state[pieceId] = { where: 'tray', placedCorrect: false };
      }
    }
    return state;
  }

  /** Для save guard: число объектов в placedLayer (0 если нет слоя). */
  getPlacedLayerCount(): number {
    return (this.placedLayer?.list?.length ?? 0) as number;
  }
  getPlacedPiecesCount(): number {
    return this.placedPieces.size;
  }

  private findPlacedImageByPieceId(pieceId: number): Phaser.GameObjects.Image | null {
    if (!this.placedLayer) return null;
    const children = this.placedLayer.list as Phaser.GameObjects.GameObject[];
    for (const obj of children) {
      if (obj.getData?.('pieceId') === pieceId) return obj as Phaser.GameObjects.Image;
    }
    return null;
  }

  /**
   * Возвращает стороны данного кусочка, вдоль которых есть уже правильно поставленный сосед
   * (для эффекта свечения шва при склейке).
   */
  private getSeamSidesWithCorrectNeighbor(pieceId: number): SeamSide[] {
    const sides: SeamSide[] = [];
    const row = Math.floor(pieceId / this.boardCols);
    const col = pieceId % this.boardCols;
    if (row > 0 && this.placedCorrectPieceIds.has(pieceId - this.boardCols)) sides.push('top');
    if (col < this.boardCols - 1 && this.placedCorrectPieceIds.has(pieceId + 1)) sides.push('right');
    if (row < this.boardRows - 1 && this.placedCorrectPieceIds.has(pieceId + this.boardCols)) sides.push('bottom');
    if (col > 0 && this.placedCorrectPieceIds.has(pieceId - 1)) sides.push('left');
    return sides;
  }

  /**
   * Проверка "правильного соседства" в world-координатах:
   * кусочки должны быть соседями по ID и визуально стоять с ожидаемым смещением.
   */
  private areCorrectlyAdjacentWorld(aId: number, bId: number): boolean {
    const rowA = Math.floor(aId / this.boardCols);
    const colA = aId % this.boardCols;
    const rowB = Math.floor(bId / this.boardCols);
    const colB = bId % this.boardCols;

    // Должны быть соседями в правильной сетке (по ID).
    const gridDistance = Math.abs(rowB - rowA) + Math.abs(colB - colA);
    if (gridDistance !== 1) return false;

    const aImg = this.findPlacedImageByPieceId(aId);
    const bImg = this.findPlacedImageByPieceId(bId);
    if (!aImg || !bImg) return false;

    // Для соседства центры деталей должны отличаться на шаг сетки (cell), а не на полный размер текстуры куска.
    const { w: cellW, h: cellH } = this.getCellSize();
    const expectedDx = (colB - colA) * cellW;
    const expectedDy = (rowB - rowA) * cellH;
    const actualDx = bImg.x - aImg.x;
    const actualDy = bImg.y - aImg.y;
    const eps = Math.max(14, cellW * 0.12);
    const dxErr = Math.abs(actualDx - expectedDx);
    const dyErr = Math.abs(actualDy - expectedDy);
    return dxErr < eps && dyErr < eps;
  }

  private playOutlineVfx(pieceId: number, worldX: number, worldY: number, sides: SeamSide[]): void {
    if (sides.length === 0 || !this.callbacks.playSeamGlow) return;
    this.callbacks.playSeamGlow({
      pieceId,
      worldX,
      worldY,
      sides,
      pieceW: this.boardPieceW,
      pieceH: this.boardPieceH,
    });
    this.callbacks.onMagnetJoin?.();
  }

  private getSeamSideBetweenNeighbors(pieceId: number, otherId: number): SeamSide | null {
    const row = Math.floor(pieceId / this.boardCols);
    const col = pieceId % this.boardCols;
    const otherRow = Math.floor(otherId / this.boardCols);
    const otherCol = otherId % this.boardCols;
    if (otherRow === row - 1 && otherCol === col) return 'top';
    if (otherRow === row + 1 && otherCol === col) return 'bottom';
    if (otherRow === row && otherCol === col - 1) return 'left';
    if (otherRow === row && otherCol === col + 1) return 'right';
    return null;
  }

  /**
   * Лёгкий "magnet join": если после дропа группа почти попала в корректное относительное
   * положение с соседним незафиксированным куском — дотягиваем до точной стыковки.
   */
  private tryMagnetJoin(group: number[]): boolean {
    const movableGroup = group.filter((id) => !this.placedCorrectPieceIds.has(id));
    if (movableGroup.length === 0) return false;

    const groupSet = new Set<number>(movableGroup);
    const nonFixedOthers: number[] = [];
    for (const [id] of this.placedPieces) {
      if (this.placedCorrectPieceIds.has(id)) continue;
      if (groupSet.has(id)) continue;
      nonFixedOthers.push(id);
    }
    if (nonFixedOthers.length === 0) return false;

    const { w: cellW, h: cellH } = this.getCellSize();
    const joinRadius = Math.max(22, cellW * 0.16);
    let best:
      | {
          inGroupId: number;
          otherId: number;
          errorDx: number;
          errorDy: number;
          errorDist: number;
        }
      | null = null;

    for (const inGroupId of movableGroup) {
      const inImg = this.findPlacedImageByPieceId(inGroupId);
      if (!inImg) continue;
      const inRow = Math.floor(inGroupId / this.boardCols);
      const inCol = inGroupId % this.boardCols;

      for (const otherId of nonFixedOthers) {
        const otherImg = this.findPlacedImageByPieceId(otherId);
        if (!otherImg) continue;
        const otherRow = Math.floor(otherId / this.boardCols);
        const otherCol = otherId % this.boardCols;
        if (Math.abs(otherRow - inRow) + Math.abs(otherCol - inCol) !== 1) continue;

        const expectedDx = (otherCol - inCol) * cellW;
        const expectedDy = (otherRow - inRow) * cellH;
        const actualDx = otherImg.x - inImg.x;
        const actualDy = otherImg.y - inImg.y;
        const errorDx = actualDx - expectedDx;
        const errorDy = actualDy - expectedDy;
        const errorDist = Math.hypot(errorDx, errorDy);
        if (errorDist > joinRadius) continue;

        if (!best || errorDist < best.errorDist) {
          best = { inGroupId, otherId, errorDx, errorDy, errorDist };
        }
      }
    }

    if (!best) return false;

    for (const id of movableGroup) {
      const im = this.findPlacedImageByPieceId(id);
      if (im) im.setPosition(im.x + best.errorDx, im.y + best.errorDy);
    }

    const inAfter = this.findPlacedImageByPieceId(best.inGroupId);
    const otherAfter = this.findPlacedImageByPieceId(best.otherId);
    if (inAfter && otherAfter) {
      const seamSide = this.getSeamSideBetweenNeighbors(best.inGroupId, best.otherId);
      if (seamSide) {
        // playSeamGlow рассчитывает шов от центра pieceId, поэтому для join
        // передаём центр "якорного" куска, а не midpoint между кусками.
        this.playOutlineVfx(best.inGroupId, inAfter.x, inAfter.y, [seamSide]);
      }
    }
    return true;
  }

  /**
   * Возвращает список pieceId кусочков, образующих с данным одну "правильно собранную" группу
   * (соседи по доске с правильным относительным положением; только не зафиксированные).
   */
  private getGroupForPiece(pieceId: number): number[] {
    if (this.placedCorrectPieceIds.has(pieceId)) return [pieceId];
    const nonFixed = new Set<number>();
    for (const [id] of this.placedPieces) {
      if (!this.placedCorrectPieceIds.has(id)) nonFixed.add(id);
    }
    if (!nonFixed.has(pieceId)) return [pieceId];

    const visited = new Set<number>();
    const queue: number[] = [pieceId];
    visited.add(pieceId);
    while (queue.length > 0) {
      const id = queue.shift()!;
      for (const otherId of nonFixed) {
        if (visited.has(otherId)) continue;
        if (!this.areCorrectlyAdjacentWorld(id, otherId)) continue;
        visited.add(otherId);
        queue.push(otherId);
      }
    }
    return [...visited];
  }

  /** Прямоугольник слота (target) для кусочка в мировых координатах. */
  private getSlotRect(fixedPieceId: number): { x: number; y: number; w: number; h: number } | null {
    return this.getCorrectTargetRect(fixedPieceId);
  }

  /** Bounds кусочка по центру и display size (origin 0.5). */
  private getPieceBoundsRect(img: Phaser.GameObjects.Image): { x: number; y: number; w: number; h: number } {
    const w = img.displayWidth;
    const h = img.displayHeight;
    return { x: img.x - w / 2, y: img.y - h / 2, w, h };
  }

  private static overlapArea(
    a: { x: number; y: number; w: number; h: number },
    b: { x: number; y: number; w: number; h: number }
  ): number {
    const left = Math.max(a.x, b.x);
    const right = Math.min(a.x + a.w, b.x + b.w);
    const top = Math.max(a.y, b.y);
    const bottom = Math.min(a.y + a.h, b.y + b.h);
    if (left >= right || top >= bottom) return 0;
    return (right - left) * (bottom - top);
  }

  /**
   * После фиксации куска в слот: если какой-то незафиксированный кусок сильно перекрывает этот слот,
   * возвращаем его в лоток, чтобы два куска не занимали один слот.
   */
  private evictPiecesOverlappingSlot(fixedPieceId: number): void {
    const slotRect = this.getSlotRect(fixedPieceId);
    if (!slotRect) return;

    const toEvict: number[] = [];
    for (const [id] of this.placedPieces) {
      if (id === fixedPieceId || this.placedCorrectPieceIds.has(id)) continue;
      const img = this.findPlacedImageByPieceId(id);
      if (!img || !img.active) continue;
      const pieceRect = this.getPieceBoundsRect(img);
      const pieceArea = pieceRect.w * pieceRect.h;
      if (pieceArea <= 0) continue;
      const overlap = BoardController.overlapArea(slotRect, pieceRect);
      const ratio = overlap / pieceArea;
      if (ratio > BoardController.OVERLAP_EVICT_THRESHOLD) toEvict.push(id);
    }

    const { w: tileW, h: tileH } = this.getPieceSize();
    for (const id of toEvict) {
      const im = this.findPlacedImageByPieceId(id);
      if (!im) continue;
      const pieceKey =
        (im.getData('pieceKey') as string) ||
        (im.texture.key.endsWith(BOARD_PIECE_TEXTURE_SUFFIX)
          ? im.texture.key.slice(0, -BOARD_PIECE_TEXTURE_SUFFIX.length)
          : im.texture.key);
      im.destroy();
      this.unplacePiece(id);
      this.callbacks.returnBoardPieceToTray(id, pieceKey, tileW, tileH);
    }
    if (toEvict.length > 0) {
      this.callbacks.updatePuzzleProgressDOM();
      this.callbacks.saveState();
    }
  }

  private handleBoardDragEnd(img: Phaser.GameObjects.Image): void {
    const group = this.dragGroup ?? [img.getData('pieceId') as number];
    const rect = this.getBoardRectGeom();

    // Если хотя бы один кусок группы вышел за доску — возвращаем всю группу в лоток
    if (rect) {
      let anyOut = false;
      for (const pieceId of group) {
        const im = this.findPlacedImageByPieceId(pieceId);
        if (im && !rect.contains(im.x, im.y)) {
          anyOut = true;
          break;
        }
      }
      if (anyOut) {
        const { w: tileW, h: tileH } = this.getPieceSize();
        for (const pieceId of group) {
          const im = this.findPlacedImageByPieceId(pieceId);
          if (im) {
            const pieceKey =
              (im.getData('pieceKey') as string) ||
              (im.texture.key.endsWith(BOARD_PIECE_TEXTURE_SUFFIX)
                ? im.texture.key.slice(0, -BOARD_PIECE_TEXTURE_SUFFIX.length)
                : im.texture.key);
            im.destroy();
            this.unplacePiece(pieceId);
            this.callbacks.returnBoardPieceToTray(pieceId, pieceKey, tileW, tileH);
          }
        }
        this.dragGroup = null;
        this.dragGroupStartPositions.clear();
        this.callbacks.saveState();
        return;
      }
    }

    // Проверяем снап и обновляем позиции по каждому куску группы
    const fixedInThisRound: number[] = [];
    for (const pieceId of group) {
      const im = this.findPlacedImageByPieceId(pieceId);
      if (!im) continue;
      const target = this.targetsByPieceId.get(pieceId);
      if (target) {
        const dist = Phaser.Math.Distance.Between(im.x, im.y, target.targetX, target.targetY);
        if (dist < this.snapRadius) {
          im.setPosition(target.targetX, target.targetY);
          im.disableInteractive();
          this.scene.input.setDraggable(im, false);
          im.setData('placedCorrect', true);
          im.setDepth(BoardController.BOARD_FIXED_PIECE_DEPTH);
          this.syncContainerOrder(im, 'fixed');
          this.placedCorrectPieceIds.add(pieceId);
          this.placedPieces.set(pieceId, {
            row: Math.floor(pieceId / this.boardCols),
            col: pieceId % this.boardCols,
          });
          this.callbacks.playFloatingCheckmark(target.targetX, target.targetY);
          this.playOutlineVfx(pieceId, target.targetX, target.targetY, this.getSeamSidesWithCorrectNeighbor(pieceId));
          fixedInThisRound.push(pieceId);
        }
      }
      if (!this.placedCorrectPieceIds.has(pieceId)) {
        im.setDepth(BoardController.BOARD_FREE_PIECE_DEPTH);
        this.syncContainerOrder(im, 'free');
        const cell = this.worldToCell(im.x, im.y);
        if (cell) this.placedPieces.set(pieceId, { row: cell.row, col: cell.col });
      }
    }
    for (const id of fixedInThisRound) {
      this.evictPiecesOverlappingSlot(id);
    }

    // Если финального снапа не произошло, пробуем "магнитное" соединение соседних кластеров.
    this.tryMagnetJoin(group);

    this.callbacks.updatePuzzleProgressDOM();
    this.callbacks.saveState();
    this.dragGroup = null;
    this.dragGroupStartPositions.clear();
  }
}

